import React, { useState, useEffect } from "react";
import { RiCloseLine } from "react-icons/ri";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getAllPointOfSales, getProducts, getPublicProducts, updateTransfer } from "UpdatedFeatures/api";
import { toast } from "react-toastify";
import { Puff } from "react-loader-spinner";
import { Autocomplete, TextField } from "@mui/material";

const EditTransferModal = ({ open, setOpen, selectedTransfer }) => {
    const queryClient = useQueryClient();
    const [senderPosId, setSenderPosId] = useState("");
    const [receivers, setReceivers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [activeSearchField, setActiveSearchField] = useState(null);
    const [searchTerms, setSearchTerms] = useState({});

    // Fetch all point of sales
    const { data: posData, isLoading: posLoading } = useQuery({
        queryKey: ["pointOfSales"],
        queryFn: () => getAllPointOfSales(),
        enabled: open
    });

    // Fetch products
    const { data: PublicProducts, isLoading: productsLoading } = useQuery({
        queryKey: ["products", searchTerm],
        queryFn: () => getPublicProducts(searchTerm, null, "", "", "", null, "", "", [], false),
        enabled: open && searchTerm.length > 2
    });
    //   const { data: PublicProducts, isLoading: productsLoading } = useQuery({
    //     queryKey: ["products"],
    //     queryFn: () => getPublicProducts(),
    //   });
    // Group transfert_receivers by POS
    const groupReceiversByPos = (transfertReceivers) => {
        if (!transfertReceivers) return [];

        const posGroups = {};

        transfertReceivers.forEach(receiver => {
            const posId = receiver.receiver_pos_id;

            if (!posGroups[posId]) {
                posGroups[posId] = {
                    receiver_pos_id: posId,
                    receiver_pos: receiver.receiver_pos,
                    receiver_pos_name: receiver.receiver_pos_name,
                    products: []
                };
            }

            // Only include products that are not fully transferred (status !== 1)
            if (receiver.status !== 1) {
                posGroups[posId].products.push({
                    id: receiver.id,
                    product_id: receiver.product_id,
                    quantity: receiver.quantity,
                    original_quantity: receiver.quantity,
                    product_title: `Nom du Produit: ${receiver.product_title}`
                });
            }
        });

        return Object.values(posGroups);
    };

    // Initialize form with selected transfer data
    useEffect(() => {
        if (selectedTransfer && open) {
            setSenderPosId(selectedTransfer.sender_pos_id);

            // Group receivers by POS
            const groupedReceivers = groupReceiversByPos(selectedTransfer.transfert_receivers);

            // Only include receivers with editable products
            const filteredReceivers = groupedReceivers.filter(receiver => receiver.products.length > 0);

            setReceivers(filteredReceivers);
        }
    }, [selectedTransfer, open]);

    // Update transfer mutation
    const updateTransferMutation = useMutation({
        mutationFn: updateTransfer,
        onSuccess: () => {
            toast.success("Transfert modifié avec succès");
            queryClient.invalidateQueries(["transfers"]);
            setOpen(false);
        },
        onError: (error) => {
            toast.error(`Erreur lors de la modification du transfert: La quantité Demandéé du Point de vente d'origine n'est pas disponible.`);
            setIsLoading(false);
        }
    });

    // Add a product to a receiver
    const addProduct = (receiverIndex) => {
        const newReceivers = [...receivers];
        newReceivers[receiverIndex].products.push({ product_id: "", quantity: 1 });
        setReceivers(newReceivers);
    };

    // Remove a product from a receiver
    const removeProduct = (receiverIndex, productIndex) => {
        const newReceivers = [...receivers];
        newReceivers[receiverIndex].products.splice(productIndex, 1);
        setReceivers(newReceivers);
    };

    // Update product details
    const updateProduct = (receiverIndex, productIndex, field, value) => {
        const newReceivers = [...receivers];
        newReceivers[receiverIndex].products[productIndex][field] = field === "quantity" ? parseInt(value) : value;
        setReceivers(newReceivers);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Validate form
        for (let i = 0; i < receivers.length; i++) {
            for (let j = 0; j < receivers[i].products.length; j++) {
                if (!receivers[i].products[j].product_id) {
                    toast.error(`Veuillez sélectionner un produit pour le groupe ${i + 1}, produit ${j + 1}`);
                    setIsLoading(false);
                    return;
                }
                if (receivers[i].products[j].quantity <= 0) {
                    toast.error(`La quantité doit être supérieure à 0 pour le groupe ${i + 1}, produit ${j + 1}`);
                    setIsLoading(false);
                    return;
                }
            }
        }

        // Create payload with the correct structure
        const payload = {
            receivers: receivers.map(receiver => ({
                receiver_pos_id: receiver.receiver_pos_id,
                products: receiver.products.map(product => ({
                    product_id: parseInt(product.product_id),
                    quantity: parseInt(product.quantity)
                }))
            }))
        };

        updateTransferMutation.mutate({
            transfer_id: selectedTransfer.id,
            ...payload
        });
    };

    if (!open) return null;

    // Check if transfer is editable
    const isEditable = selectedTransfer && selectedTransfer.status !== 1;

    if (!isEditable) {
        return (
            <div
                className="fixed inset-0 z-50"
                style={{ zIndex: 9999, overflow: "auto" }}
                aria-labelledby="modal-title"
                role="dialog"
                aria-modal="true"
            >
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75"
                    aria-hidden="true"
                    onClick={() => setOpen(false)}
                />
                <div className="flex items-center justify-center min-h-screen p-4">
                    <div
                        className="relative w-full max-w-md mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30">
                            <h3 id="modal-title" className="text-lg font-medium leading-6 text-white">
                                Modification impossible
                            </h3>
                            <button
                                type="button"
                                className="inline-flex items-center justify-center p-1.5 text-white hover:text-gray-200"
                                onClick={() => setOpen(false)}
                            >
                                <RiCloseLine className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p className="text-gray-700">
                                Ce transfert ne peut pas être modifié car il est déjà entièrement transféré.
                            </p>
                            <div className="mt-6 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#b269ae] hover:bg-[#a058a0] focus:outline-none"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 z-50"
            style={{ zIndex: 9999, overflow: "auto" }}
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                aria-hidden="true"
                onClick={() => setOpen(false)}
            />

            {/* Modal Content */}
            <div className="flex items-center justify-center min-h-screen p-4">
                <div
                    className="relative w-full max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30">
                        <h3 id="modal-title" className="text-lg font-medium leading-6 text-white">
                            Modifier le Transfert
                        </h3>
                        <button
                            type="button"
                            className="inline-flex items-center justify-center p-1.5 text-white hover:text-gray-200 hover:scale-105 duration-200"
                            onClick={() => setOpen(false)}
                        >
                            <RiCloseLine className="h-6 w-6" aria-hidden="true" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Sender POS Information (read-only) */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Point de vente d'origine
                            </label>
                            <input
                                type="text"
                                value={selectedTransfer.sender_pos == null
                                    ? selectedTransfer.sender_pos_name || "KD Marché"
                                    : selectedTransfer.sender_pos.name}
                                disabled
                                className="w-full p-3 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed"
                            />
                            <p className="mt-1 text-sm text-gray-500">Le point de vente d'origine ne peut pas être modifié.</p>
                        </div>

                        {/* Receivers Section */}
                        <div className="space-y-8">
                            <h4 className="text-lg font-medium text-gray-700">Points de vente destinataires</h4>

                            {receivers.length === 0 ? (
                                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                    <p className="text-gray-500">Tous les produits ont déjà été transférés. Aucune modification n'est possible.</p>
                                </div>
                            ) : (
                                receivers.map((receiver, receiverIndex) => (
                                    <div key={receiverIndex} className="p-4 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
                                        <div className="flex justify-between items-center mb-4">
                                            <h5 className="text-md font-medium text-gray-700">
                                                Destinataire: {receiver.receiver_pos == null && receiver.receiver_pos_name == null ? "KD Marché" : receiver.receiver_pos_name}
                                            </h5>
                                        </div>

                                        {/* Products Section */}
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <h6 className="text-sm font-medium text-gray-700">Produits</h6>
                                                {/* <button
                                                    type="button"
                                                    onClick={() => addProduct(receiverIndex)}
                                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs leading-4 font-medium rounded-md text-white bg-[#b269ae] hover:bg-[#a058a0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#b269ae]"
                                                >
                                                    <FaPlus className="mr-1" /> Ajouter un produit
                                                </button> */}
                                            </div>

                                            {receiver.products.map((product, productIndex) => (
                                                <div key={productIndex} className="grid grid-cols-12 gap-4 items-center bg-white p-3 rounded-md shadow-sm">
                                                    {/* Product Selection/Display */}
                                                    <div className="col-span-8">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Produit
                                                        </label>
                                                        {product.original_quantity ? (
                                                            // For existing products, show as read-only
                                                            <input
                                                                type="text"
                                                                value={product.product_title}
                                                                disabled
                                                                className="w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed"
                                                            />
                                                        ) : (
                                                            // For new products, use Autocomplete component
                                                            <Autocomplete
                                                                options={PublicProducts?.data || []}
                                                                getOptionLabel={(option) => option.title || ""}
                                                                onChange={(event, newValue) => {
                                                                    if (newValue) {
                                                                        updateProduct(receiverIndex, productIndex, "product_id", newValue.id);
                                                                        updateProduct(receiverIndex, productIndex, "product_title", newValue.title);
                                                                    }
                                                                }}
                                                                loading={productsLoading}
                                                                loadingText="Chargement des produits..."
                                                                noOptionsText="Aucun produit trouvé"
                                                                slotProps={{ popper: { sx: { zIndex: 10000 } } }}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder="Rechercher un produit..."
                                                                        variant="outlined"
                                                                        size="small"
                                                                        fullWidth
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            endAdornment: (
                                                                                <>
                                                                                    {productsLoading ? <Puff color="#b269ae" height={20} width={20} /> : null}
                                                                                    {params.InputProps.endAdornment}
                                                                                </>
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    </div>

                                                    {/* Quantity Input */}
                                                    <div className="col-span-3">
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            Quantité
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={product.quantity}
                                                            onChange={(e) => updateProduct(receiverIndex, productIndex, "quantity", e.target.value)}
                                                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-[#b269ae] focus:border-[#b269ae]"
                                                        />
                                                    </div>

                                                    {/* Remove Product Button */}
                                                    <div className="col-span-1 flex items-end justify-center">
                                                        {!product.original_quantity && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeProduct(receiverIndex, productIndex)}
                                                                className="text-red-500 hover:text-red-700 p-2"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="mt-8 flex justify-end">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading || updateTransferMutation.isPending || receivers.length === 0}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#b269ae] hover:bg-[#a058a0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#b269ae] disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {updateTransferMutation.isPending ? (
                                    <>
                                        <Puff color="white" height={16} width={16} className="mr-2" />
                                        Modification en cours...
                                    </>
                                ) : (
                                    "Enregistrer les modifications"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditTransferModal; 